import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { AlertTriangle, CalendarPlus, CheckCircle2, ChevronRight, Loader2, RefreshCw, ShieldCheck, Archive } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Chip } from "./chip";
import { useMinWidth } from "./checks-panel";
import { EmptyState } from "./item-ui";
import { SlotDialog } from "./slot-dialog";
import {
  CHANNEL_LABEL,
  PLAN_CHANNELS,
  PUBLISHING_SETTINGS_HREF,
  SLOT_STATUS_CLASS,
  SLOT_STATUS_LABEL,
  blogCadenceOf,
  blogMixByWeek,
  channelConnected,
  connectionName,
  defaultPlanStart,
  describePolicy,
  describeShare,
  fmtPeriod,
  fmtShortDay,
  matrixWith,
  mixMatches,
  overLimitWeeks,
  parseDayKey,
  planSlotCounts,
  startOfWeek,
  updateTargetLabel,
  useAgenticActions,
  useNow,
  usePersistedJson,
  usePlans,
  usePublishPolicy,
  type BlogCadence,
  type Plan,
  type PlanChannel,
  type PlanSlot,
  type PublishPolicy,
  type SlotStatus,
  type WeekMix,
} from "./agentic";
import { dayKey, fmtDateTime } from "./helpers";
import { useWorkspaceRole } from "./use-studio";
import { useWorkspaceStore } from "@/hooks/use-workspace";

const BUILD_POLL_MS = 15_000;
const BUILD_STALE_MS = 45 * 60_000;
const REVIEW_STALE_MS = 30 * 60_000;

/** What the tab is waiting for the Claude team to produce. Kept in localStorage so a reload does not lose the banner. */
interface Watch {
  building?: { startedAt: number; knownIds: string[]; weeks: number };
  reviews?: Record<string, { startedAt: number; prevAt: string | null }>;
}

const NOUN: Record<PlanChannel, [string, string]> = {
  blog: ["blog post", "blog posts"],
  linkedin: ["LinkedIn post", "LinkedIn posts"],
  x: ["X post", "X posts"],
  facebook: ["Facebook post", "Facebook posts"],
  email: ["email", "emails"],
};

function planStatusChip(plan: Plan): { label: string; cls: string } {
  switch (plan.status) {
    case "draft": return { label: "Draft, needs your approval", cls: "border-amber-500/30 bg-amber-500/15 text-amber-200" };
    case "reviewed": return { label: "Reviewed, ready for your approval", cls: "border-sky-500/30 bg-sky-500/15 text-sky-200" };
    case "approved": return { label: "Active", cls: "border-emerald-500/30 bg-emerald-500/15 text-emerald-200" };
    default: return { label: "Archived", cls: "border-zinc-500/30 bg-zinc-500/15 text-zinc-300" };
  }
}

function verdictView(verdict: string): { label: string; cls: string } {
  const v = verdict.toLowerCase();
  if (/reject|block|not ready|fail/.test(v)) return { label: "Not ready", cls: "border-red-500/30 bg-red-500/15 text-red-200" };
  if (/revis|change|needs|improv|concern/.test(v)) return { label: "Changes suggested", cls: "border-amber-500/30 bg-amber-500/15 text-amber-200" };
  if (/approv|good|ok|pass|ready|looks/.test(v)) return { label: "Looks good", cls: "border-emerald-500/30 bg-emerald-500/15 text-emerald-200" };
  return { label: verdict ? verdict[0].toUpperCase() + verdict.slice(1) : "Reviewed", cls: "border-border bg-muted/40 text-foreground/80" };
}

// ── Matrix ──────────────────────────────────────────────────────────────────

function SlotChip({ slot, onClick }: { slot: PlanSlot; onClick: (s: PlanSlot) => void }) {
  return (
    <button
      type="button"
      onClick={() => onClick(slot)}
      className={`w-full rounded-md px-2 py-1.5 text-left text-xs leading-snug focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 ${SLOT_STATUS_CLASS[slot.status]}`}
      data-testid={`slot-${slot.slotId}`}
      data-status={slot.status}
      title={`${slot.topic} (${SLOT_STATUS_LABEL[slot.status]})`}
    >
      <span className="block text-[10px] font-semibold opacity-80">{fmtShortDay(slot.date)}</span>
      <span className="line-clamp-2 break-words">{slot.topic || "Untitled"}</span>
      {slot.kind === "update" && (
        <span className="mt-0.5 block break-all text-[10px] font-semibold text-violet-300" data-testid={`slot-update-${slot.slotId}`}>
          ↻ updates {updateTargetLabel(slot) ?? "an existing post"}
        </span>
      )}
      <span className="mt-0.5 block text-[10px] opacity-75">{SLOT_STATUS_LABEL[slot.status]}</span>
    </button>
  );
}

/** One week's blog posts: how many are new and how many update a live post, against the policy's cadence. */
function WeekBlogSummary({ mix, cadence }: { mix: WeekMix | undefined; cadence: BlogCadence }) {
  if (!mix) return null;
  const over = cadence.blogsPerWeek !== null && mix.total > cadence.blogsPerWeek;
  const offMix = cadence.newShare !== null && !mixMatches(mix.newCount, mix.total, cadence.newShare);
  return (
    <div className="mt-1 space-y-0.5 text-[11px] font-normal normal-case tracking-normal" data-testid={`week-mix-${mix.weekStart}`} data-over={over} data-off-mix={offMix}>
      <p className="text-foreground/80">
        Blog: {mix.newCount} new · {mix.updateCount} {mix.updateCount === 1 ? "update" : "updates"}
      </p>
      {over && (
        <p className="flex items-start gap-1 text-amber-300" role="status" data-testid={`week-over-${mix.weekStart}`}>
          <AlertTriangle size={11} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            {mix.total} blog posts; your policy allows {cadence.blogsPerWeek} a week
          </span>
        </p>
      )}
      {!over && offMix && cadence.newShare !== null && <p className="text-muted-foreground">Policy is {describeShare(cadence.newShare)}</p>}
    </div>
  );
}

function PlanMatrix({ plan, onSlot, cadence }: { plan: Plan; onSlot: (s: PlanSlot) => void; cadence: BlogCadence }) {
  const wide = useMinWidth(640);
  const mixes = useMemo(() => blogMixByWeek(plan), [plan]);
  const { weeks, channels } = useMemo(() => {
    const byWeek = new Map<string, PlanSlot[]>();
    for (const s of plan.matrix) {
      const k = dayKey(startOfWeek(parseDayKey(s.date.slice(0, 10))));
      byWeek.set(k, [...(byWeek.get(k) ?? []), s]);
    }
    const keys = Array.from(byWeek.keys()).sort();
    return {
      weeks: keys.map((k) => ({ key: k, slots: byWeek.get(k)! })),
      channels: PLAN_CHANNELS.filter((c) => plan.matrix.some((s) => s.channel === c)),
    };
  }, [plan.matrix]);

  if (plan.matrix.length === 0) {
    return <p className="text-sm text-muted-foreground">This plan has no slots yet.</p>;
  }

  const weekLabel = (k: string) => {
    const a = parseDayKey(k);
    return `Week of ${a.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  };

  if (!wide) {
    return (
      <div className="space-y-3" data-testid="plan-matrix" data-layout="stacked">
        {weeks.map((w) => (
          <section key={w.key} className="space-y-2 rounded-xl border border-border bg-card/40 p-3" data-testid={`plan-week-${w.key}`}>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{weekLabel(w.key)}</h4>
              <WeekBlogSummary mix={mixes.get(w.key)} cadence={cadence} />
            </div>
            {channels.map((c) => {
              const slots = w.slots.filter((s) => s.channel === c);
              if (slots.length === 0) return null;
              return (
                <div key={c} className="space-y-1.5">
                  <p className="text-xs font-medium text-foreground/80">{CHANNEL_LABEL[c]}</p>
                  <div className="grid gap-1.5">
                    {slots.map((s) => <SlotChip key={s.slotId} slot={s} onClick={onSlot} />)}
                  </div>
                </div>
              );
            })}
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border" data-testid="plan-matrix" data-layout="table">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <thead>
          <tr className="bg-muted/40">
            <th className="w-36 px-3 py-2 text-[11px] font-medium text-muted-foreground">Week</th>
            {channels.map((c) => (
              <th key={c} className="px-2 py-2 text-[11px] font-medium text-muted-foreground">{CHANNEL_LABEL[c]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => (
            <tr key={w.key} className="border-t border-border align-top" data-testid={`plan-week-${w.key}`}>
              <th scope="row" className="px-3 py-2 text-left text-xs font-medium text-foreground/80">
                {weekLabel(w.key).replace("Week of ", "")}
                <WeekBlogSummary mix={mixes.get(w.key)} cadence={cadence} />
              </th>
              {channels.map((c) => (
                <td key={c} className="px-2 py-2">
                  <div className="grid gap-1.5">
                    {w.slots.filter((s) => s.channel === c).map((s) => <SlotChip key={s.slotId} slot={s} onClick={onSlot} />)}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The whole plan's blog mix, and a warning when a week holds more blog posts than the policy allows. */
function BlogMixSummary({ plan, cadence }: { plan: Plan; cadence: BlogCadence }) {
  const weeks = useMemo(() => Array.from(blogMixByWeek(plan).values()), [plan]);
  if (weeks.length === 0) return null;
  const nNew = weeks.reduce((n, w) => n + w.newCount, 0);
  const nUpdate = weeks.reduce((n, w) => n + w.updateCount, 0);
  const over = overLimitWeeks(plan, cadence.blogsPerWeek);
  const policyBits = [cadence.newShare !== null ? describeShare(cadence.newShare) : null, cadence.blogsPerWeek !== null ? `up to ${cadence.blogsPerWeek} a week` : null].filter(Boolean);
  return (
    <div className="space-y-1" data-testid="plan-blog-mix">
      <p className="text-xs text-muted-foreground">
        Blog: {nNew} new, {nUpdate} {nUpdate === 1 ? "update" : "updates"}
        {policyBits.length > 0 ? ` (your policy: ${policyBits.join(", ")})` : ""}
      </p>
      {over.length > 0 && (
        <p className="flex items-start gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100" role="status" data-testid="plan-over-limit">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            {over.length === 1 ? "One week holds" : `${over.length} weeks hold`} more blog posts than your policy allows ({cadence.blogsPerWeek} a week). Skip a slot, or{" "}
            <Link href={PUBLISHING_SETTINGS_HREF} className="underline underline-offset-2">raise the limit in Settings → Publishing</Link>.
          </span>
        </p>
      )}
    </div>
  );
}

function SlotSummary({ plan }: { plan: Plan }) {
  const counts = useMemo(() => {
    const c: Record<SlotStatus, number> = { planned: 0, ordered: 0, drafted: 0, gated: 0, published: 0, skipped: 0 };
    for (const s of plan.matrix) c[s.status] += 1;
    return c;
  }, [plan.matrix]);
  const parts = (Object.keys(counts) as SlotStatus[]).filter((k) => counts[k] > 0).map((k) => `${counts[k]} ${SLOT_STATUS_LABEL[k].toLowerCase()}`);
  return <p className="text-xs text-muted-foreground" data-testid="plan-summary">{plan.matrix.length} slots{parts.length ? `: ${parts.join(", ")}` : ""}</p>;
}

// ── Approval summary ────────────────────────────────────────────────────────

function ApprovalSummary({ plan, policy, workspace }: { plan: Plan; policy: PublishPolicy | null; workspace: any }) {
  const counts = planSlotCounts(plan);
  const rows = PLAN_CHANNELS.filter((c) => counts[c] > 0);
  const overWeeks = overLimitWeeks(plan, blogCadenceOf(policy).blogsPerWeek);
  return (
    <div className="space-y-3 text-sm" data-testid="approval-summary">
      <p>
        {plan.matrix.filter((s) => s.status !== "skipped").length} pieces between {fmtPeriod(plan)}. Draper orders each one two days before its date, and from then on:
      </p>
      <ul className="space-y-1.5">
        {rows.map((c) => {
          const p = policy?.[c];
          const mode = p?.mode ?? "manual";
          const connected = channelConnected(workspace, c);
          return (
            <li key={c} className="rounded-lg border border-border bg-muted/20 px-3 py-2" data-testid={`approval-row-${c}`}>
              <span className="font-semibold text-foreground">{counts[c]} {NOUN[c][counts[c] === 1 ? 0 : 1]}</span>
              <span className="text-foreground/80">: each {describePolicy(p)}.</span>
              {mode !== "manual" && connected === false && (
                <span className="block pt-0.5 text-xs text-amber-300">{connectionName(c)} isn't connected, so these won't go out by themselves until you connect it in Settings → Publishing.</span>
              )}
            </li>
          );
        })}
      </ul>
      {!policy && (
        <p className="text-xs text-muted-foreground">Your publishing policy isn't available right now, so every piece is treated as Manual: it waits for your approval.</p>
      )}
      {overWeeks.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-300" data-testid="approval-over-limit">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            More blog posts than your policy allows ({blogCadenceOf(policy).blogsPerWeek} a week) in the week of{" "}
            {overWeeks.map((w) => parseDayKey(w.weekStart).toLocaleDateString(undefined, { month: "short", day: "numeric" })).join(", ")}.
          </span>
        </p>
      )}
      <p className="text-xs text-muted-foreground">If another approved plan covers the same dates, it is archived. You can still hold or reject any piece, and archiving a plan stops anything not yet ordered.</p>
    </div>
  );
}

// ── One plan ────────────────────────────────────────────────────────────────

function PlanCard({
  plan,
  policy,
  workspace,
  canApprove,
  reviewWait,
  onAskReview,
  onApprove,
  onArchive,
  onSlot,
  busy,
  now,
}: {
  plan: Plan;
  policy: PublishPolicy | null;
  workspace: any;
  canApprove: boolean;
  reviewWait: { startedAt: number } | null;
  onAskReview: (p: Plan) => void;
  onApprove: (p: Plan) => void;
  onArchive: (p: Plan) => void;
  onSlot: (plan: Plan, slot: PlanSlot) => void;
  busy: { review: boolean; approve: boolean; archive: boolean };
  now: number;
}) {
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const cadence = useMemo(() => blogCadenceOf(policy), [policy]);
  const chip = planStatusChip(plan);
  const open = plan.status === "draft" || plan.status === "reviewed";
  const review = plan.review;
  const verdict = review ? verdictView(review.verdict) : null;
  const waitingMin = reviewWait ? Math.max(0, Math.floor((now - reviewWait.startedAt) / 60_000)) : 0;
  const reviewStale = !!reviewWait && now - reviewWait.startedAt > REVIEW_STALE_MS;

  const approveButton = (
    <Button type="button" disabled={!canApprove || busy.approve} onClick={() => setConfirmApprove(true)} className="bg-emerald-600 text-white hover:bg-emerald-600/90" data-testid={`button-approve-plan-${plan.id}`}>
      {busy.approve ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
      Approve plan
    </Button>
  );

  return (
    <section className="space-y-4 rounded-2xl border border-border bg-card/70 p-4 sm:p-5" data-testid={`plan-card-${plan.id}`} data-status={plan.status}>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip className={chip.cls}>{chip.label}</Chip>
          <Chip className="border-border bg-muted/40 text-foreground/80">{fmtPeriod(plan)}</Chip>
          {plan.approvedAt && fmtDateTime(plan.approvedAt) && <Chip className="border-border bg-muted/40 text-foreground/80">Approved {fmtDateTime(plan.approvedAt)}</Chip>}
        </div>
        <h3 className="break-words text-lg font-semibold leading-snug text-foreground" data-testid="plan-title">{plan.title}</h3>
        <SlotSummary plan={plan} />
        <BlogMixSummary plan={plan} cadence={cadence} />
      </div>

      {open && (
        <p className="text-sm text-muted-foreground">
          Nothing is written until you approve this plan. Tap a slot to change its topic or angle first.
        </p>
      )}

      {reviewWait && (
        <p className="flex items-start gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm text-sky-100" role="status" data-testid="review-waiting">
          <Loader2 size={14} className="mt-0.5 shrink-0 animate-spin" />
          <span>
            {reviewStale
              ? "The supervisor is taking longer than usual. The Claude team may be busy or offline; the verdict will still appear here."
              : `The supervisor is reviewing this plan${waitingMin > 0 ? ` (${waitingMin} min so far)` : ""}. The verdict appears here, usually within a few minutes.`}
          </span>
        </p>
      )}

      {review && verdict && (
        <div className="space-y-2 rounded-xl border border-border bg-muted/20 p-3" data-testid="plan-review">
          <div className="flex flex-wrap items-center gap-2">
            <ShieldCheck size={15} className="text-muted-foreground" />
            <h4 className="text-sm font-semibold text-foreground">Supervisor review</h4>
            <Chip className={verdict.cls} title={review.verdict}>{verdict.label}</Chip>
            <span className="text-xs text-muted-foreground">
              {review.by}
              {review.at && fmtDateTime(review.at) ? `, ${fmtDateTime(review.at)}` : ""}
            </span>
          </div>
          {review.notes.length > 0 && (
            <ul className="list-disc space-y-1 pl-5 text-sm text-foreground/90" data-testid="review-notes">
              {review.notes.map((n, i) => <li key={i} className="break-words">{n}</li>)}
            </ul>
          )}
          {review.suggestedChanges.length > 0 && (
            <div className="space-y-1" data-testid="review-suggestions">
              <p className="text-xs font-semibold text-foreground/80">Suggested changes</p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-foreground/90">
                {review.suggestedChanges.map((n, i) => <li key={i} className="break-words">{n}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <PlanMatrix plan={plan} onSlot={(s) => onSlot(plan, s)} cadence={cadence} />

      {(open || plan.status === "approved") && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4" data-testid="plan-actions">
          {open && (
            <>
              <Button type="button" variant="outline" disabled={busy.review || !!reviewWait} onClick={() => onAskReview(plan)} data-testid={`button-request-review-${plan.id}`}>
                {busy.review ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {review ? "Ask the supervisor to review again" : "Ask the supervisor to review"}
              </Button>
              {canApprove ? (
                approveButton
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60" data-testid="approve-plan-disabled">
                      {approveButton}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">Only the workspace owner or an admin can approve a plan.</TooltipContent>
                </Tooltip>
              )}
            </>
          )}
          <Button type="button" variant="ghost" disabled={busy.archive || !canApprove} onClick={() => setConfirmArchive(true)} className="sm:ml-auto" data-testid={`button-archive-plan-${plan.id}`}>
            {busy.archive ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />}
            Archive
          </Button>
          {!canApprove && <p className="w-full text-xs text-muted-foreground" data-testid="note-plan-owner-only">Only the workspace owner or an admin can approve or archive a plan.</p>}
        </div>
      )}

      <AlertDialog open={confirmApprove} onOpenChange={setConfirmApprove}>
        <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve “{plan.title}”?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <ApprovalSummary plan={plan} policy={policy} workspace={workspace} />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={() => onApprove(plan)} data-testid={`button-confirm-approve-plan-${plan.id}`}>Approve plan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this plan?</AlertDialogTitle>
            <AlertDialogDescription>
              {plan.status === "approved"
                ? "Draper stops ordering new pieces from it. Pieces that are already written or waiting to publish are not removed; hold or reject those yourself."
                : "It moves to the plan history. You can generate a new one any time."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => onArchive(plan)} data-testid={`button-confirm-archive-plan-${plan.id}`}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

// ── Tab ─────────────────────────────────────────────────────────────────────

export function PlanTab({ workspace, onOpenItem }: { workspace: any; onOpenItem: (id: number) => void }) {
  const { toast } = useToast();
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const { role } = useWorkspaceRole();
  const canApprove = role === null ? true : role === "owner" || role === "admin";
  const now = useNow(60_000);

  const [watch, setWatch] = usePersistedJson<Watch>(wsId ? `drpr-plan-watch-${wsId}` : null);
  const polling = !!watch?.building || Object.keys(watch?.reviews ?? {}).length > 0;
  const { plans, available, isLoading, isError, refetch } = usePlans({ pollMs: polling ? BUILD_POLL_MS : false });
  const { policy } = usePublishPolicy();
  const { generatePlan, requestReview, approvePlan, archivePlan, patchPlan } = useAgenticActions();

  const [startDate, setStartDate] = useState(() => defaultPlanStart());
  const [slotKey, setSlotKey] = useState<{ planId: string; slotId: string } | null>(null);

  // Settle the "waiting" banners as soon as the thing they wait for shows up.
  useEffect(() => {
    if (isLoading || !watch) return;
    const next: Watch = { ...watch };
    let changed = false;
    if (watch.building) {
      const fresh = plans.find((p) => !watch.building!.knownIds.includes(String(p.id)));
      if (fresh) {
        delete next.building;
        changed = true;
        toast({ title: "Your plan is ready for review", description: `“${fresh.title}” is waiting under Plan. Read it, then approve it.` });
      }
    }
    if (watch.reviews) {
      const reviews = { ...watch.reviews };
      for (const [id, w] of Object.entries(reviews)) {
        const plan = plans.find((p) => String(p.id) === id);
        if (!plan || (plan.status !== "draft" && plan.status !== "reviewed")) {
          delete reviews[id];
          changed = true;
        } else if (plan.review?.at && plan.review.at !== w.prevAt) {
          delete reviews[id];
          changed = true;
          toast({ title: "The supervisor has reviewed the plan", description: "The verdict is on the plan below." });
        }
      }
      if (changed) next.reviews = reviews;
    }
    if (changed) setWatch(next.building || Object.keys(next.reviews ?? {}).length ? next : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, isLoading]);

  const open = useMemo(() => plans.filter((p) => p.status === "draft" || p.status === "reviewed"), [plans]);
  const approved = useMemo(() => plans.filter((p) => p.status === "approved"), [plans]);
  const active = approved[0] ?? null;
  const history = useMemo(() => [...approved.slice(1), ...plans.filter((p) => p.status === "archived")], [approved, plans]);

  const building = watch?.building ?? null;
  const buildStale = !!building && now - building.startedAt > BUILD_STALE_MS;
  const buildMin = building ? Math.max(0, Math.floor((now - building.startedAt) / 60_000)) : 0;

  const generate = (weeks: 2 | 4) =>
    generatePlan.mutate(
      { weeks, startDate },
      {
        onSuccess: () => setWatch({ ...(watch ?? {}), building: { startedAt: Date.now(), knownIds: plans.map((p) => String(p.id)), weeks } }),
      },
    );

  const askReview = (plan: Plan) =>
    requestReview.mutate(plan.id, {
      onSuccess: () =>
        setWatch({ ...(watch ?? {}), reviews: { ...(watch?.reviews ?? {}), [String(plan.id)]: { startedAt: Date.now(), prevAt: plan.review?.at ?? null } } }),
    });

  const slotPlan = slotKey ? plans.find((p) => String(p.id) === slotKey.planId) ?? null : null;
  const slot = slotPlan && slotKey ? slotPlan.matrix.find((s) => s.slotId === slotKey.slotId) ?? null : null;
  const editable = !!slotPlan && (slotPlan.status === "draft" || slotPlan.status === "reviewed") && slot?.status === "planned";

  const saveSlot = (patch: { topic: string; angle: string }) => {
    if (!slotPlan || !slot) return;
    patchPlan.mutate(
      { planId: slotPlan.id, matrix: matrixWith(slotPlan, slot.slotId, patch) },
      {
        onSuccess: () => {
          setSlotKey(null);
          toast({ title: "Slot updated" });
        },
      },
    );
  };
  const skipSlot = () => {
    if (!slotPlan || !slot) return;
    patchPlan.mutate(
      { planId: slotPlan.id, matrix: matrixWith(slotPlan, slot.slotId, { status: "skipped" }) },
      {
        onSuccess: () => {
          setSlotKey(null);
          toast({ title: "Slot skipped", description: "Draper won't write anything for it." });
        },
      },
    );
  };

  const renderCard = (plan: Plan) => (
    <PlanCard
      key={String(plan.id)}
      plan={plan}
      policy={policy}
      workspace={workspace}
      canApprove={canApprove}
      reviewWait={watch?.reviews?.[String(plan.id)] ?? null}
      onAskReview={askReview}
      onApprove={(p) => approvePlan.mutate(p.id)}
      onArchive={(p) => archivePlan.mutate(p.id)}
      onSlot={(p, s) => setSlotKey({ planId: String(p.id), slotId: s.slotId })}
      busy={{
        review: requestReview.isPending && requestReview.variables === plan.id,
        approve: approvePlan.isPending && approvePlan.variables === plan.id,
        archive: archivePlan.isPending && archivePlan.variables === plan.id,
      }}
      now={now}
    />
  );

  return (
    <div className="space-y-6" data-testid="plan-tab">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Content plan</h2>
        <p className="text-sm text-muted-foreground">
          The plan says what Draper's team writes and when. You approve the plan once; the team then orders each piece two days before its date and publishes it the way your publishing policy says.
        </p>
      </div>

      {/* Generate */}
      <section className="space-y-3 rounded-2xl border border-border bg-card/50 p-4" data-testid="plan-generate">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label htmlFor="plan-start" className="text-xs font-medium text-muted-foreground">Starts on</label>
            <input
              id="plan-start"
              type="date"
              value={startDate}
              min={dayKey(new Date())}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              data-testid="input-plan-start"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {([2, 4] as const).map((w) => (
              <Button
                key={w}
                type="button"
                variant={w === 2 ? "default" : "outline"}
                disabled={!startDate || generatePlan.isPending || !!building || !available}
                onClick={() => generate(w)}
                data-testid={`button-generate-${w}`}
              >
                {generatePlan.isPending && generatePlan.variables?.weeks === w ? <Loader2 size={14} className="animate-spin" /> : <CalendarPlus size={14} />}
                Generate a new plan ({w} weeks)
              </Button>
            ))}
          </div>
        </div>
        {!available && !isLoading && (
          <p className="text-xs text-muted-foreground" data-testid="plans-unavailable">Content plans aren't available on this server yet.</p>
        )}
        {building && (
          <div className="flex items-start gap-2 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.06] px-3 py-2 text-sm text-foreground/90" role="status" aria-live="polite" data-testid="plan-building">
            <Loader2 size={14} className="mt-0.5 shrink-0 animate-spin text-cyan-400" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-foreground">
                {buildStale ? "The plan is taking longer than usual" : "Draper is building the plan"}
              </p>
              <p className="text-xs text-foreground/70">
                {buildStale
                  ? "The Claude team may be busy or offline. The plan will still appear here for review when it is done."
                  : `It will appear here for review (usually 10–15 min)${buildMin > 0 ? `. Started ${buildMin} min ago.` : "."}`}
              </p>
            </div>
            {buildStale && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setWatch(watch?.reviews && Object.keys(watch.reviews).length ? { reviews: watch.reviews } : null)} data-testid="button-dismiss-building">
                Dismiss
              </Button>
            )}
          </div>
        )}
      </section>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 animate-spin" size={18} /> Loading…
        </div>
      ) : isError ? (
        <div className="space-y-3 py-12 text-center" data-testid="plans-error">
          <p className="text-sm text-foreground">Couldn't load your plans.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>Try again</Button>
        </div>
      ) : (
        <>
          {open.length > 0 && (
            <div className="space-y-4" data-testid="plans-open">
              <h3 className="text-base font-semibold text-foreground">Waiting for your approval</h3>
              {open.map(renderCard)}
            </div>
          )}

          {active ? (
            <div className="space-y-4" data-testid="plan-active">
              <h3 className="text-base font-semibold text-foreground">Active plan</h3>
              {renderCard(active)}
            </div>
          ) : (
            open.length === 0 &&
            !building &&
            available && (
              <EmptyState title="There is no plan yet.">
                Generate a 2-week or 4-week plan above. Draper builds it, you review it, and the team takes it from there.
              </EmptyState>
            )
          )}

          {history.length > 0 && (
            <details className="group rounded-xl border border-border bg-card/40" data-testid="plan-history">
              <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground [&::-webkit-details-marker]:hidden">
                <ChevronRight size={16} className="text-muted-foreground transition-transform group-open:rotate-90" />
                Plan history
                <span className="font-normal text-muted-foreground">{history.length}</span>
              </summary>
              <div className="space-y-4 border-t border-border p-3 sm:p-4">{history.map(renderCard)}</div>
            </details>
          )}
        </>
      )}

      <SlotDialog
        open={slotKey !== null && !!slot}
        onOpenChange={(o) => !o && setSlotKey(null)}
        slot={slot}
        plan={slotPlan}
        editable={editable}
        saving={patchPlan.isPending}
        onSave={saveSlot}
        onSkip={slotPlan && slotPlan.status !== "archived" ? skipSlot : undefined}
        onOpenItem={(id) => {
          setSlotKey(null);
          onOpenItem(id);
        }}
      />
    </div>
  );
}

// Re-exported so the studio shell can show "a plan is waiting" on the tab without importing the whole tab.
export function planNeedsApproval(plans: Plan[]): number {
  return plans.filter((p) => p.status === "draft" || p.status === "reviewed").length;
}
